<template>
  <v-dialog v-model="showEditPopup" persistent scrollable max-width="920" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <v-form class="fdlg__form" @submit.prevent="updateUser">
        <div class="fdlg__head">
          <div>
            <h2 class="fdlg__title">{{ firstName }} {{ lastName }}</h2>
            <p class="fdlg__sub">Editați datele, rolul și departamentele contului.</p>
          </div>
          <v-btn icon aria-label="Închide" @click="showEditPopup = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text ref="body" class="fdlg__body">
          <section class="form-section">
            <div class="form-section__label"><v-icon small>mdi-account-outline</v-icon>Persoana</div>
            <v-row dense>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="userName"
                  label="Login*"
                  prepend-inner-icon="mdi-login"
                  outlined
                  :error-messages="formErrors.userName"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field v-model="firstName" label="Numele*" outlined :error-messages="formErrors.firstName"></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field v-model="lastName" label="Prenumele*" outlined :error-messages="formErrors.lastName"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="email"
                  label="E-mail*"
                  prepend-inner-icon="mdi-email-outline"
                  outlined
                  :error-messages="formErrors.email"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <v-text-field v-model="telephone" label="Telefon mobil" prepend-inner-icon="mdi-cellphone" outlined :counter="9"></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="landline"
                  label="Telefon fix*"
                  prepend-inner-icon="mdi-phone-outline"
                  outlined
                  :error-messages="formErrors.landline"
                ></v-text-field>
              </v-col>
            </v-row>
          </section>

          <section class="form-section">
            <div class="form-section__label"><v-icon small>mdi-shield-account-outline</v-icon>Rolul în aplicație</div>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="selectedRole"
                  :items="roleItems"
                  label="Rol*"
                  item-text="label"
                  item-value="id"
                  outlined
                  :error-messages="formErrors.selectedRole"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-switch v-model="isActive" label="Contul este activ" inset color="primary" hide-details class="mt-2"></v-switch>
              </v-col>
            </v-row>
          </section>

          <section class="form-section">
            <div class="form-section__label"><v-icon small>mdi-domain</v-icon>Departamente</div>
            <p class="form-section__note">
              O persoană poate lucra în mai multe departamente și poate fi șef în oricare dintre ele. Totul se vede într-un singur cont.
            </p>
            <p v-if="legacyMainDepartment" class="form-section__note">
              Departamentul principal rămâne «{{ legacyMainDepartment }}» (nu este în lista departamentelor).
            </p>
            <v-progress-linear v-if="loadingDepartments" indeterminate color="primary" class="mb-3"></v-progress-linear>
            <DepartmentsEditor
              v-else
              v-model="deptRows"
              :main-id.sync="mainDepartmentId"
              :departments="departmentOptions"
              :allow-sef="roleAllowsSef"
              :error="departmentsError"
            />
            <div class="mb-3"></div>
          </section>
        </v-card-text>

        <div class="fdlg__foot">
          <v-btn outlined large color="primary" @click="showConfirmCard()">
            <v-icon left>mdi-lock-reset</v-icon>Parolă nouă
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text large @click="showEditPopup = false">Anulare</v-btn>
          <v-btn class="btn-green" large type="submit" :loading="saving">
            <v-icon left>mdi-content-save-outline</v-icon>Salvează
          </v-btn>
        </div>
      </v-form>
      <ConfirmCard
        v-if="confirmCard"
        title="Generați o parolă nouă? Ea va fi trimisă pe e-mailul utilizatorului."
        @cancel="cancelConfirmCard"
        @confirm="newPassword"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import * as api from "./api";
import * as helper from "@/helper/helper.js";
import ConfirmCard from "@/components/elements/ConfirmCard.vue";
import DepartmentsEditor from "./DepartmentsEditor.vue";
import userDepartmentsMixin from "./userDepartmentsMixin";
import { mapGetters } from "vuex";

export default {
  components: {
    ConfirmCard,
    DepartmentsEditor,
  },
  mixins: [userDepartmentsMixin],
  props: {
    user: Object,
  },
  data() {
    return {
      roles: [],
      showEditPopup: false,
      saving: false,
      loadingDepartments: false,
      selectedRole: null,
      userName: "",
      firstName: "",
      lastName: "",
      editedUser: { ...this.user },
      email: "",
      isActive: false,
      isSefBoth: false,
      sefRoleId: 4,
      landline: "",
      telephone: "",
      confirmCard: false,
      formErrors: {
        selectedRole: "",
        userName: "",
        firstName: "",
        lastName: "",
        email: "",
        landline: "",
        telephone: "",
      },
    };
  },
  methods: {
    show() {
      this.showEditPopup = true;
      helper.scrollToTop(this);
      this.fetchData();
    },
    async fetchData() {
      this.loadingDepartments = true;
      try {
        const [roles] = await Promise.all([api.fetchRoles(), this.loadDepartmentOptions()]);
        this.roles = roles;
        if (this.user && this.user.id) {
          // the table row only has names; ids and sef flags come from the user record
          const detail = await api.getUserById(this.user.id);
          this.setDepartmentsFromUser(detail.departments, detail.department);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        this.$emit("error", "Datele utilizatorului nu au putut fi încărcate");
      } finally {
        this.loadingDepartments = false;
      }
    },
    async updateUser() {
      this.validateForm();
      const departmentsOk = this.validateDepartments();
      if (!this.isFormValid() || !departmentsOk) {
        return;
      }

      const formattedLandline = helper.validateLandline(this.landline);
      if (!formattedLandline) {
        this.formErrors.landline = "Telefon fix invalid";
        return;
      }

      const userDataToUpdate = {
        id: this.editedUser.id,
        role_id: this.selectedRole,
        user_name: this.userName,
        first_name: this.firstName,
        last_name: this.lastName,
        department: this.mainDepartmentName(),
        departments: this.departmentsPayload(),
        email: this.email,
        isActive: this.isActive,
        isSefBoth: this.selectedRole === this.sefRoleId ? this.isSefBoth : false,
        landline: formattedLandline,
        telephone: this.telephone,
      };

      if (!userDataToUpdate.id) {
        console.error("No user ID found for updating. userDataToUpdate:", userDataToUpdate);
        return;
      }

      this.saving = true;
      try {
        await api.updateUser(userDataToUpdate);
        this.showEditPopup = false;
        this.$emit("success", "Modificările au fost salvate");
        this.$emit("updateUserData");
      } catch (error) {
        console.error("Error updating user:", error);
        this.$emit("error", error.response?.data?.message || "Modificările nu au fost salvate. Încercați din nou.");
      } finally {
        this.saving = false;
      }
    },
    resetForm() {
      this.formErrors = {
        selectedRole: "",
        userName: "",
        firstName: "",
        lastName: "",
        email: "",
        landline: "",
        telephone: "",
      };
      this.resetDepartments();
    },
    validateForm() {
      this.formErrors.selectedRole = this.selectedRole ? "" : "Alegeți un rol";
      this.formErrors.userName = this.userName ? "" : "Loginul este obligatoriu";
      this.formErrors.firstName = this.firstName ? "" : "Numele este obligatoriu";
      this.formErrors.lastName = this.lastName ? "" : "Prenumele este obligatoriu";
      this.formErrors.email = helper.validateEmail(this.email) ? "" : "Adresa de e-mail nu este validă";
    },
    isFormValid() {
      return !Object.values(this.formErrors).some((error) => error);
    },
    cancelConfirmCard() {
      this.confirmCard = false;
    },
    showConfirmCard() {
      this.confirmCard = true;
    },
    async newPassword() {
      try {
        await api.generateNewPassword(this.user.id);
        this.$emit("success", "Parola nouă a fost trimisă pe e-mailul utilizatorului");
        this.confirmCard = false;
        this.showEditPopup = false;
      } catch (error) {
        console.error("Error generating password:", error);
        this.showEditPopup = false;
        this.$emit("error", "Parola nouă nu a putut fi generată");
      }
    },
  },
  watch: {
    showEditPopup(newVal) {
      if (!newVal) {
        this.resetForm();
      }
    },
    user: {
      immediate: true,
      handler(newValue) {
        if (newValue && Object.keys(newValue).length > 0) {
          this.editedUser = { ...newValue };
          this.selectedRole = newValue.roleId ? newValue.roleId.id : null;
          this.userName = newValue.user_name;
          this.firstName = newValue.first_name;
          this.lastName = newValue.last_name;
          this.email = newValue.email;
          this.isActive = newValue.isActive === "Activ";
          this.isSefBoth = !!newValue.isSefBoth;
          this.telephone = newValue.telephone;
          this.landline = newValue.landline;
        }
      },
    },
    selectedRole(newValue) {
      this.editedUser.role_id = newValue || null;
      if (newValue) this.formErrors.selectedRole = "";
    },
    userName() {
      this.formErrors.userName = "";
    },
    firstName() {
      this.formErrors.firstName = "";
    },
    lastName() {
      this.formErrors.lastName = "";
    },
    email() {
      this.formErrors.email = "";
    },
    telephone() {
      this.formErrors.telephone = "";
    },
    landline() {
      this.formErrors.landline = "";
    },
  },
  computed: {
    ...mapGetters(["getUser", "isAdmin"]),
  },
};
</script>
